import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, PenTool, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amount);

export const OrderSignPage = () => {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { fetchOrderData(); }, [token]);
  useEffect(() => { if (orderData && canvasRef.current) initCanvas(); }, [orderData]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const fetchOrderData = async () => {
    try {
      const res = await fetch(`${API}/orders/sign/${token}`);
      if (res.status === 404) { setError("invalid"); return; }
      if (res.status === 409) { setError("already_signed"); return; }
      if (!res.ok) throw new Error();
      setOrderData(await res.json());
    } catch {
      setError("invalid");
    } finally {
      setLoading(false);
    }
  };

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    if (isDrawing && canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL("image/png"));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => { initCanvas(); setSignatureData(""); };

  const handleSubmit = async () => {
    if (!signatureData) { toast.error("Please sign before submitting"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/orders/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_data: signatureData }),
      });
      if (!res.ok) {
        let msg = "Failed to submit";
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        throw new Error(msg);
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || "Failed to submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 animate-pulse text-sm">Loading...</div>
      </div>
    );
  }

  if (error === "invalid") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4 text-3xl">❌</div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Invalid Link</h1>
          <p className="text-slate-500 text-sm">This signing link is invalid or has expired. Please contact your RM.</p>
        </div>
      </div>
    );
  }

  if (error === "already_signed") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Already Signed</h1>
          <p className="text-slate-500 text-sm">This invoice has already been signed. No further action is needed.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Signature Submitted!</h1>
          <p className="text-slate-500 text-sm">Thank you. Your signature has been recorded and the invoice is now confirmed.</p>
        </div>
      </div>
    );
  }

  const invoice = orderData?.invoice;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#0047FF] flex items-center justify-center">
            <PenTool className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-slate-900 text-sm">Pokus — Invoice Sign-off</h1>
            <p className="text-xs text-slate-500">{orderData?.order_number}</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Order Summary */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Order Summary</p>
          </div>
          <div className="p-4 space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Customer</span>
              <span className="font-medium text-slate-900">{orderData?.customer_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Service</span>
              <span className="font-medium text-slate-900">{orderData?.service_type}</span>
            </div>
            {orderData?.merchant_name && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Merchant</span>
                <span className="font-medium text-slate-900">{orderData.merchant_name}</span>
              </div>
            )}
            {orderData?.items_submitted && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Items</span>
                <span className="font-medium text-slate-900 text-right max-w-[60%]">{orderData.items_submitted}</span>
              </div>
            )}
          </div>
        </div>

        {/* Invoice Details */}
        {invoice && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Invoice Details</p>
              <span className="text-xs font-mono text-slate-500">{invoice.invoice_number}</span>
            </div>
            <div className="p-4 space-y-2">
              {invoice.line_items?.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-700">{item.custom_service || item.service_type} × {item.quantity}</span>
                  <span className="font-mono text-slate-800">{formatCurrency(item.line_total)}</span>
                </div>
              ))}
              <div className="border-t border-slate-100 pt-2 mt-2 flex justify-between text-sm font-bold">
                <span className="text-slate-900">Total Payable</span>
                <span className="font-mono text-[#0047FF]">{formatCurrency(invoice.total_payable)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Signature Canvas */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Merchant Signature</p>
            <button
              onClick={clearSignature}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>
          <div className="p-3">
            <p className="text-xs text-slate-400 mb-2 text-center">Sign below to confirm the above invoice</p>
            <canvas
              ref={canvasRef}
              width={560}
              height={180}
              className="w-full border border-dashed border-slate-300 rounded-lg bg-white touch-none"
              style={{ cursor: "crosshair" }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !signatureData}
          className="w-full py-4 rounded-xl bg-[#0047FF] text-white font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Confirm & Submit Signature
            </>
          )}
        </button>

        <p className="text-center text-xs text-slate-400 pb-4">
          By signing, the merchant confirms the above services and invoice amount.
        </p>
      </div>
    </div>
  );
};
